import { formatDateTime } from '@simpleacs/shared';
/**
 * The vocabulary the billing pages share.
 *
 * Pulled out of `Billing.tsx` when the invoice and payment detail views
 * arrived: three pages naming the same statuses in three places is how "void"
 * ends up red on one screen and grey on the next, and how a period reads
 * "1 Aug" here and "01/08/2026" there.
 */

/**
 * `1 August 2026`, in UTC.
 *
 * Every billing boundary in this platform is a UTC month start. Rendering one
 * in the reader's own zone is how "1 September" becomes "31 August" for
 * somebody west of Greenwich, on a document whose whole job is to name a
 * period exactly.
 */
export const monthDate = (ts: number): string =>
  new Date(ts).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

/**
 * `1 August 2026 – 31 August 2026`.
 *
 * The stored end is EXCLUSIVE — the first instant not covered — so the last
 * day it actually covers is a millisecond earlier. Printing the stored value
 * would claim one day more than was sold.
 */
export const periodLabel = (start: number | null, end: number | null): string =>
  start && end ? `${monthDate(start)} – ${monthDate(end - 1)}` : '—';

export const STATUS_TONE = {
  pending: 'warn',
  confirmed: 'success',
  failed: 'danger',
  rejected: 'danger',
} as const;

export const METHOD_LABEL = {
  mpesa: 'M-PESA',
  stripe: 'Stripe',
  paystack: 'Paystack',
  bank: 'Bank',
  // Not a gateway: the bill was settled out of the organization's prepay
  // balance, with money that arrived earlier by one of the four above. The
  // wallet's own ledger says which.
  credit: 'Credit',
} as const;

/**
 * `2 Aug 2026, 14:31` — a payment's timestamp, in a fixed format.
 *
 * Not `toLocaleString()` with no locale, which is what the payments list used
 * to print. That renders in whatever locale the BROWSER is set to, so the same
 * column reads `8/2/2026` for one operator and `02.08.2026` for the next, and
 * the day and the month change places between them. A billing list where two
 * people disagree about which number is the month is not a list anybody can
 * read out to each other.
 *
 * On the platform's clock (EAT): a payment is an instant, not a period
 * boundary, and printed in UTC every M-PESA receipt read three hours early
 * (operator, 2026-10-05: "those timestamps are wrong"). `monthDate` above
 * stays UTC because it names a UTC month start.
 */
export const dateTime = (ts: number): string => formatDateTime(ts);

export const INVOICE_TONE: Record<string, 'neutral' | 'info' | 'success' | 'danger'> = {
  draft: 'neutral',
  issued: 'info',
  paid: 'success',
  void: 'danger',
};
